// Your circles (07, Design A; 33; 36; v1.58.35): the switcher, from the circle tile — a popover beside
// the rail on a computer and a tablet, a sheet from the top on a phone (✕, the scrim, Escape and Back
// close it; no swipe). Focus goes to the open circle's row, ↑ / ↓ move, and it comes back to the
// tile. In the installed app ⌘ / Ctrl with 1 to 9 switch to that circle (in a browser they're its tabs').
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
/** You's "Switch circle" (07). */
export { default as switchIcon } from '@material-symbols/svg-400/outlined/swap_horiz.svg?raw';
import { IconButton } from '../blocks/Button';
import { Dialog } from '../blocks/Dialog';
import { icons } from '../blocks/icons';
import { useScreen } from '../blocks/Page';
import { config } from '../core/config';
import { CircleFace } from '../frame/CircleTile';
import { InvitationsDialog } from '../id/Invitations';
import { isInstalled } from '../install/install';
import { t } from '../i18n';
import { CircleItems, useCircleRows, type Circles, type CircleRow } from './AccountsView';
import './circles.css';

const mac = /Mac|iPhone|iPad/.test(navigator.platform);

let from: HTMLElement | undefined;
// The press on the tile that closed it (a press outside the popover) doesn't open it again.
let closed = { tile: undefined as HTMLElement | undefined, at: 0 };
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

/** Opens Your circles from `tile` (its focus comes back there when it closes). */
export function openCircles(tile: HTMLElement): void {
  if (closed.tile === tile && performance.now() - closed.at < 300) return;
  from = tile;
  changed();
}

/** Closes Your circles. */
export function closeCircles(): void {
  from?.focus();
  from = undefined;
  changed();
}

/** Your circles is open. */
export const useCirclesOpen = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => !!from);

/** The Back that takes a closed sheet's entry away, until it has landed. */
let leaving: Promise<void> | undefined;

/**
 * A phone's sheet has a history entry while open (`on`), so Back closes it (07, 34). A sheet opened
 * again at once adds its entry only once the last one's has gone, or that Back would close it.
 */
export function useBackCloses(close: () => void, on = true) {
  const latest = useRef(close);
  latest.current = close;
  useEffect(() => {
    if (!on) return;
    let live = true;
    let entry = false;
    const onPop = () => entry && !history.state?.sheet && ((entry = false), latest.current());
    void (leaving ?? Promise.resolve()).then(() => {
      if (!live) return;
      history.pushState({ ...history.state, sheet: true }, '');
      entry = true;
      addEventListener('popstate', onPop);
    });
    return () => {
      live = false;
      removeEventListener('popstate', onPop);
      if (!entry) return;
      leaving = new Promise((done) => addEventListener('popstate', () => ((leaving = undefined), done()), { once: true }));
      history.back();
    };
  }, [on]);
}

/** The header, the rows and the keys, in the popover or the sheet. */
function Content({ client, circles, phone, onDialog }: { client: MatrixClient; circles: Circles; phone: boolean; onDialog: (d: 'invitations') => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.closest('dialog')?.setAttribute('data-desyr-circles', 'sheet');
    ref.current?.querySelector<HTMLElement>('[aria-current="true"]')?.focus();
  }, []);
  const n = Math.min(circles.rows.length, 9);
  const key = mac ? '⌘' : 'Ctrl+';
  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest('.menu')) return; // the row menu keeps its keys
    if (e.key === 'Escape' && !phone) return void (e.preventDefault(), closeCircles());
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = [...ref.current!.querySelectorAll<HTMLElement>('.circle-pick')];
    const at = list.indexOf(document.activeElement as HTMLElement);
    list[at < 0 ? 0 : (at + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length]?.focus();
  };
  const pick = (r: CircleRow) => (closeCircles(), circles.pick(r));
  return (
    <div ref={ref} className="circles-content" onKeyDown={onKeyDown}>
      <header className="circles-head">
        {phone && <CircleFace userId={circles.me} size={48} />}
        <h2>{t('id.circles')}</h2>
        {!phone && isInstalled() && n > 1 && <span className="circles-keys">{`${key}1 – ${key}${n}`}</span>}
        {phone && <IconButton label={t('common.close')} icon={icons.close} onClick={closeCircles} />}
      </header>
      <CircleItems client={client} circles={circles} onPick={pick} onClose={closeCircles} onDialog={onDialog} />
    </div>
  );
}

/** On a computer and a tablet: beside the rail, its top 6 px above the tile's; a press outside or Escape closes it. */
function Popover({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current!;
    const tile = from!.getBoundingClientRect();
    el.showPopover?.();
    el.style.left = `${(from!.closest('.rail')?.getBoundingClientRect().right ?? tile.right) + 10}px`;
    el.style.top = `${Math.max(8, tile.top - 6)}px`;
    // Only a press on the tile itself counts as the one that closed it (not Escape, a choice or a press elsewhere).
    let onTile = false;
    const onDown = (e: PointerEvent) => void (onTile = !!from?.contains(e.target as Node));
    const onToggle = (e: Event) => (e as ToggleEvent).newState === 'closed' && ((closed = { tile: onTile ? from : undefined, at: performance.now() }), closeCircles());
    addEventListener('pointerdown', onDown, true);
    el.addEventListener('toggle', onToggle);
    return () => void (removeEventListener('pointerdown', onDown, true), el.removeEventListener('toggle', onToggle), el.matches?.(':popover-open') && el.hidePopover());
  }, []);
  return (
    <div ref={ref} className="circles" role="dialog" aria-label={t('id.circles')} data-desyr-circles="popover" popover="auto">
      {children}
    </div>
  );
}

/** On a phone: the sheet from the top over the scrim. */
function Sheet({ children }: { children: ReactNode }) {
  useBackCloses(closeCircles);
  return <Dialog label={t('id.circles')} className="circles sheet" onClose={closeCircles}>{children}</Dialog>;
}

/** Draws Your circles while it is open; the installed app's ⌘ / Ctrl+1–9 always. */
export function YourCirclesHost({ client }: { client: MatrixClient }) {
  const open = useCirclesOpen();
  const phone = useScreen() === 'phone';
  const circles = useCircleRows(client);
  const [dialog, setDialog] = useState<'invitations'>();
  const latest = useRef(circles);
  latest.current = circles;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const row = latest.current.rows[Number(e.key) - 1];
      if (!isInstalled() || !(mac ? e.metaKey : e.ctrlKey) || e.shiftKey || e.altKey || !/^[1-9]$/.test(e.key) || !row) return;
      e.preventDefault();
      if (from) closeCircles();
      latest.current.pick(row);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);
  const content = open && <Content client={client} circles={circles} phone={phone} onDialog={setDialog} />;
  const Box = phone ? Sheet : Popover;
  return (
    <>
      {content && <Box>{content}</Box>}
      {dialog === 'invitations' && <InvitationsDialog config={config()} onClose={() => setDialog(undefined)} />}
    </>
  );
}
